import { Player } from 'soundfont-player';
import { ScalesService } from './scales.service';
import { ChordsService } from './chords.service';
import { ChordsSchemasService, Schema } from './chords-schemas.service';
import { StrumSet } from './strumming.service';

export interface ChordScale {
  type: string;
  note12: number;
  name: string;
  structure: number[]; // intervals for all schemas except struming
  noteNumbers: number[]; // note values for struming only
}

export class NoteToPlay {
    note!: number;
    volume?: number;
    delay?: number;
    duration?: number;
}

export class Sound {
  name: string;
  player: Player;
  audioContext: any;
  detune = 0;
  defaultDuration = .3;
  isPlaying = false;
  currentPlaying = -1;
  timeouts: any[] = [];
  toStop = false;
  constructor(
    name: string,
    player: Player,
    audioContext: any,
    public scalesService: ScalesService,
    public chordsService: ChordsService,
    public playSchemas: ChordsSchemasService,
  ) {
    this.name = name;
    this.player = player;
    this.audioContext = audioContext;
  }
  stop() {
    if (!this.isPlaying) {
      return;
    }
    this.toStop = true;
    this.timeouts.forEach((to) => {
      clearTimeout(to);
    });
    this.timeouts = [];
    setTimeout(() => {
      // console.log('listen.stop timeout set isPlaying false');
      this.isPlaying = false;
      this.toStop = false;
      this.currentPlaying = -1;
    }, 0);
  }
  notePlay(
    note: number,
    volume?: number,
    delay?: number,
    duration?: number) {
    if ((volume) && (volume > 17)) {
      console.log('Error volume from 0 to 17');
    }
    // console.log(this.name + ' notePlay');
    // console.log(this.noteName(note + this.detune));
    this.player
      .start(
        this.noteName(note + this.detune),
        this.audioContext.currentTime +
        (delay || 0),
        { gain: (typeof volume === 'undefined') ? 1 : volume }
      )
      .stop(
        this.audioContext.currentTime +
        (delay || 0) +
        (duration || 1)
      );
  }
  notesPlay(
    notes: NoteToPlay[]
  ) {
    let thisDelay = 0;
    if (notes.length < 1) {
      return;
    }
    notes.forEach((oneNote) => {
      thisDelay += (oneNote.delay || 0);
      this.notePlay(
        oneNote.note,
        oneNote.volume || 1,
        thisDelay,
        oneNote.duration,
    );
    });
  }
  scalePlay(
    name: string,
    start: number,
    bpm: number,
    volume: number,
    duration: number,
    withOctave: boolean,
    onEnd: any
  ) {
    let delay = 0;
    this.scalesService.getStructure(name).forEach((interval) => {
      this.notePlay(
        start + interval,
        volume,
        delay,
        duration
      );
      delay += 60 / bpm;
    });
    if (withOctave) {
      this.notePlay(
        start + 12,
        volume,
        delay,
        duration
      );
      delay += 60 / bpm;
    }
    setTimeout(onEnd, (delay + duration) * 1000);
  }
  chordPlay(  // Raw chord play. See also playChord
    name: string,
    start: number,
    inversion: number,
    arpeggio: boolean,
    bpm: number,
    volume: number,
    duration: number,
    onEnd: any
  ) {
    let delay = 0;
    let structure = this.chordsService.getStructure(name);
    if (inversion > 0) {
      structure = this.chordsService
        .getInversionStructure(name, inversion)
        .sort((n1, n2) => {
          if (n1 > n2) { return 1; }
          if (n1 < n2) { return -1; }
          return 0;
        });
    }
    structure.forEach((interval) => {
      this.notePlay(
        start + interval,
        volume,
        delay,
        duration
      );
      if (arpeggio) {
        delay += 60 / bpm;
      }
    });
    setTimeout(onEnd, (delay + duration) * 1000);
  }
  noteName(note: number) {
    return [
      'C', 'C#', 'D', 'Eb',
      'E', 'F', 'F#', 'G',
      'G#', 'A', 'Bb', 'B'][note % 12] +
      Math.floor(note / 12).toString();
  }
  isItPlaying(index: number) {
    return (this.isPlaying) && (this.currentPlaying === index);
  }
  playChord(
    chordList: ChordScale[],
    repeat: number,
    schema: Schema,
    volume: number,
    loop: number,
    bpm: number,
    octave: number,
    first: number, // First chord offset
    callbackChordPlayed: (chordIndex: number) => any,
    callbackNotePlayed: (note: number, off?: boolean) => any,
    callBackEndLoop: () => any,
    callbackEnd: () => any,
    initDelay: number,
    current: number = 0,
    strum: StrumSet, // send null if useless (no strumming)
    duration?: number,
    human?: number,
  ) {
    const durat = duration || this.defaultDuration;
    if (this.toStop) {
      if (callbackEnd) {
        callbackEnd();
      }
      return;
    }
    if (current >= chordList.length) {
      if ((loop < 0) || (loop > 1)) { // loop < 0 means infinite
        loop--;
        current = 0;
        if (callBackEndLoop) {
          callBackEndLoop();
        }
      } else  { // This was the last loop
        this.stop();
        if (callbackEnd) {
          callbackEnd();
        }
        return;
      }
    }
    this.isPlaying = true;
    this.currentPlaying = current + first;
    const chord: ChordScale = chordList[current];
    // chord.structure = this.chordsService.getStructure(chord.name);
    this.playSchemas.playChordFromSchema(schema, chord, volume, repeat, bpm, strum).forEach((ntp) => {
      if (!this.toStop) {
        const delay: number = initDelay + (ntp.delay || 0) + this.humanTime((human || 0), bpm) 
        const to1 = setTimeout(() => {
          if (!this.toStop) {
            if (callbackNotePlayed) {
              callbackNotePlayed(ntp.note);
              const to3 = setTimeout(() => {
                callbackNotePlayed(ntp.note, true);
              }, durat);
              this.timeouts.push(to3);
            }
            this.notePlay(
              ntp.note + (12 * octave),
              ntp.volume,
              0,
              durat);
          }
        }, delay);
        this.timeouts.push(to1);
      }
    });

    if (callbackChordPlayed) {
      callbackChordPlayed(current + first);
    }

    if (this.toStop) {
      if (callbackEnd) {
        callbackEnd();
      }
      return;
    }
    const schemaTime = this.playSchemas.schemaTime(schema, bpm, strum);
    const to2 = setTimeout(() => {
      this.playChord(
        chordList,
        repeat,
        schema,
        volume,
        loop,
        bpm,
        octave,
        first,
        callbackChordPlayed,
        callbackNotePlayed,
        callBackEndLoop,
        callbackEnd,
        0,
        ++current,
        strum,
        durat,
        human);
    }, initDelay + (schemaTime * repeat));
    this.timeouts.push(to2);
  }
  playChordList(
    chordList: ChordScale[],
    repeat: number,
    schema: Schema,
    volume: number,
    loop: number,
    bpm: number,
    octave: number,
    first: number, // First chord offset
    callbackChordPlayed: (chordIndex: number) => any,
    callbackNotePlayed: (note: number, off?: boolean) => any,
    callbackEndLoop: () => any,
    callbackEnd: () => any,
    strum: StrumSet, // send null if useless (no strumming)
    duration?: number,
    human?: number
  ) {
    setTimeout(() => {
      this.toStop = false;
      this.playChord(
        chordList,
        repeat,
        schema,
        volume,
        loop,
        bpm,
        octave,
        first,
        callbackChordPlayed,
        callbackNotePlayed,
        callbackEndLoop,
        callbackEnd,
        400, // Init delay
        0,
        strum,
        duration,
        human
      );
    }, 200);
  }
  humanTime(rate: number, bpm: number) {
    return rate ? (Math.random() * rate * 10000 / bpm) : 0;
  }
  startStop() {}
}
